import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { color, font } from '../styles/theme';
import { fullDate } from '../lib/format';
import { makeLookup, groupsOf, tile2 } from '../lib/presenters';
import { statusLabelOf, statusOptions } from '../lib/pipeline';
import { MESSAGE_GUIDE, isMessageForm, messageOne, messageStatusOptions } from '../lib/messageTypes';
import { useSubmission, useMeta, useSetStatus, usePeople } from '../api/queries';
import { useSetOwner } from '../api/conversation';
import { useAuth } from '../auth/AuthContext';
import { Conversation, NOT_A_MATCH, NoLetterNote } from './Conversation';
import { PeopleOptions } from '../components/ContactBits';
import {
  Modal,
  Tile,
  Eyebrow,
  Field,
  Button,
  Loading,
  ErrorNote,
  StageBadge,
  FormPill,
  RedactedBadge,
  selectStyle,
} from '../components/ui';

/**
 * One submission, opened from a table row.
 *
 * Two panes at this width: the form on the left, the conversation on the
 * right. They were stacked while the panel was a reading surface, but once
 * replying happens here, scrolling past the whole record to reach the composer
 * — and losing the message you are answering as you do — is the wrong shape.
 * The right column sticks; the record scrolls under it. Below roughly 830px
 * the grid stacks and the conversation follows the record.
 *
 * A message (a general inquiry or a request) opens with what kind of message
 * it is and how to handle it — the same words as the Messages screen — and
 * offers only the statuses a message can be in.
 *
 * The footer's Owner is anyone with an active account (`/admins/people`),
 * saved through `PATCH /submissions/{id}/owner`; a role without
 * submissions:update sees who owns the record and no select.
 */
export function SubmissionDetail({
  id,
  onClose,
  focus,
}: {
  id: string;
  onClose: () => void;
  /** Open with the composer focused — the envelope on a table row. */
  focus?: 'reply';
}) {
  const meta = useMeta();
  const detail = useSubmission(id);
  const setStatus = useSetStatus();
  const setOwner = useSetOwner();
  const qc = useQueryClient();
  const { can } = useAuth();
  const canUpdate = can('submissions:update');
  const people = usePeople(canUpdate);
  /** Set once a move to Not a match lands, so the footer can say no letter went. */
  const [noLetter, setNoLetter] = useState(false);

  const r = detail.data?.submission;
  const fm = detail.data?.meta;
  const message = !!r && !!meta.data && isMessageForm(meta.data, r.form);

  const header =
    r && fm && meta.data ? (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <FormPill form={r.form} label={message ? messageOne(meta.data, r.form) : fm.label} />
        <StageBadge stage={r.stage} label={statusLabelOf(meta.data, r.status)} />
        {r.redacted && <RedactedBadge />}
      </span>
    ) : (
      <span />
    );

  function body() {
    if (detail.isLoading || meta.isLoading) return <Loading />;
    if (detail.error) return <ErrorNote message={(detail.error as Error).message} />;
    if (!detail.data || !meta.data || !r || !fm) return null;

    const { history } = detail.data;
    const L = makeLookup(meta.data);
    const groups = groupsOf(r, L);
    const t2 = tile2(r, L);
    const guide = message ? MESSAGE_GUIDE[r.form] : undefined;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,400px),1fr))',
          gap: 28,
          alignItems: 'start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
          <div>
            <h2
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 22,
                color: color.navy,
                margin: 0,
              }}
            >
              {r.name}
            </h2>
            <p style={{ fontSize: 14, margin: '6px 0 0' }}>
              {r.redacted ? (
                <span style={{ color: color.muted }}>Contact hidden for your role</span>
              ) : (
                <a href={`mailto:${r.email}`}>{r.email}</a>
              )}
            </p>
          </div>

          {guide && (
            <div
              style={{
                background: color.tint,
                borderLeft: `3px solid ${color.blue}`,
                color: color.blueDeep,
                borderRadius: 12,
                padding: '11px 14px',
                fontSize: 12.5,
                lineHeight: '18px',
              }}
            >
              <p style={{ margin: 0 }}>
                <strong style={{ fontWeight: 800 }}>{guide.one}.</strong> {guide.what}
              </p>
              {guide.not && (
                <p style={{ margin: '6px 0 0' }}>
                  <strong style={{ fontWeight: 800 }}>Not this.</strong> {guide.not}
                </p>
              )}
              {guide.noReply && (
                <p style={{ margin: '6px 0 0' }}>
                  <strong style={{ fontWeight: 800 }}>Do not reply by email:</strong> {guide.noReply}
                </p>
              )}
              <p style={{ margin: '6px 0 0' }}>
                <strong style={{ fontWeight: 800 }}>Who answers:</strong> {fm.route} ·{' '}
                <strong style={{ fontWeight: 800 }}>Reply:</strong> {fm.sla}
              </p>
            </div>
          )}

          {r.redacted && (
            <p
              style={{
                background: color.tint,
                color: color.blueDeep,
                borderRadius: 12,
                padding: '10px 14px',
                fontSize: 12.5,
                fontWeight: 600,
                margin: 0,
              }}
            >
              Patient-identifying fields are hidden for your role. Condition and study are shown so
              you can see the workload.
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Tile label="Received" value={fullDate(r.createdAt)} />
            <Tile label={t2.label} value={t2.value} />
          </div>

          {groups.map((g) => (
            <div key={g.title}>
              <Eyebrow tone="blue">{g.title}</Eyebrow>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
                  gap: 12,
                }}
              >
                {g.fields.map((f) => (
                  <div key={f.k} style={{ minWidth: 0 }}>
                    <p style={{ fontSize: 11.5, color: color.muted, margin: '0 0 3px' }}>{f.k}</p>
                    <p style={{ fontSize: 13.5, color: color.ink, margin: 0, fontWeight: 600 }}>
                      {f.v}
                    </p>
                  </div>
                ))}
              </div>
              {g.long.map((f) => (
                <div key={f.k} style={{ marginTop: 14 }}>
                  <p style={{ fontSize: 11.5, color: color.muted, margin: '0 0 4px' }}>{f.k}</p>
                  <p
                    style={{
                      fontSize: 14,
                      lineHeight: '24px',
                      color: color.ink,
                      margin: 0,
                      background: color.bg,
                      border: `1px solid ${color.line}`,
                      borderRadius: 14,
                      padding: '14px 16px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {f.v}
                  </p>
                </div>
              ))}
            </div>
          ))}

          {!message && (
            <p style={{ fontSize: 11, color: color.muted, margin: 0 }}>
              Routes to {fm.route} · {fm.sla}
            </p>
          )}
        </div>

        {/* Sticky, so the composer stays put while the record scrolls past it. */}
        <div style={{ position: 'sticky', top: 0, minWidth: 0 }}>
          <Conversation submission={r} fm={fm} history={history} meta={meta.data} focus={focus} />
        </div>
      </div>
    );
  }

  function footer() {
    if (!meta.data || !r || !fm) return null;

    const move = (status: string) => {
      setNoLetter(false);
      setStatus.mutate(
        { id: r.id, status },
        {
          onSuccess: () => {
            setNoLetter(status === NOT_A_MATCH);
            // The move opened a clock; the clock lists should show it now.
            if (status === NOT_A_MATCH) void qc.invalidateQueries({ queryKey: ['tickets'] });
          },
        },
      );
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {setStatus.error && <ErrorNote message={(setStatus.error as Error).message} />}
        {setOwner.error && <ErrorNote message={(setOwner.error as Error).message} />}
        {noLetter && r.status === NOT_A_MATCH && <NoLetterNote after />}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
          {canUpdate ? (
            <Field label="Status" style={{ width: 190 }}>
              <select
                value={r.status}
                onChange={(e) => move(e.target.value)}
                disabled={setStatus.isPending}
                style={{ ...selectStyle, fontSize: 13 }}
              >
                {(message
                  ? messageStatusOptions(meta.data.statuses, r.status)
                  : statusOptions(meta.data.statuses, r.status)
                ).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <span style={{ fontSize: 12.5, color: color.muted, alignSelf: 'center' }}>
              Your role cannot change a submission's status or owner.
            </span>
          )}

          <Field label="Owner" style={{ width: 190 }}>
            {canUpdate ? (
              <select
                value={r.ownerId ?? ''}
                onChange={(e) => setOwner.mutate({ id: r.id, body: { ownerId: e.target.value || null } })}
                disabled={setOwner.isPending || people.isLoading}
                style={{ ...selectStyle, fontSize: 13 }}
              >
                <option value="">Unassigned</option>
                <PeopleOptions people={people.data?.people} current={{ id: r.ownerId, name: r.ownerName }} />
              </select>
            ) : (
              <span style={{ fontSize: 13, fontWeight: 600, color: r.ownerName ? color.ink : color.muted }}>
                {r.ownerName ?? 'Unassigned'}
              </span>
            )}
          </Field>

          <span style={{ flex: 1 }} />

          {canUpdate && r.status !== fm.primary.to && (
            <Button
              variant="ghost"
              onClick={() => move(fm.primary.to)}
              disabled={setStatus.isPending}
              title={`Sets the status to ${statusLabelOf(meta.data, fm.primary.to)}`}
              style={{ border: `1.5px solid ${color.blue}`, color: color.blue, fontSize: 13.5 }}
            >
              {fm.primary.label}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Modal
      header={header}
      footer={r && fm ? footer() : undefined}
      onClose={onClose}
      width={1040}
      label="Submission detail"
    >
      {body()}
    </Modal>
  );
}
